(() => {
  const rulesUrl = window.PeakStyleStructuredSearchRulesUrl;
  if (!rulesUrl) return;

  const normalize = (value) => String(value || '')
    .toLocaleLowerCase('de-DE')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\-_:/]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const containsPhrase = (text, phrase) => {
    const haystack = ` ${normalize(text)} `;
    const needle = ` ${normalize(phrase)} `;
    return haystack.includes(needle);
  };

  const findRule = (query, rules) => {
    const sorted = [...(rules || [])].sort((a, b) => {
      const longestB = Math.max(0, ...(b.aliases || []).map(alias => normalize(alias).length));
      const longestA = Math.max(0, ...(a.aliases || []).map(alias => normalize(alias).length));
      return longestB - longestA;
    });
    return sorted.find(rule => (rule.aliases || []).some(alias => containsPhrase(query, alias))) || null;
  };

  const rulesPromise = fetch(rulesUrl, {
    cache: 'no-cache',
    headers: { Accept: 'application/json' }
  })
    .then(response => {
      if (!response.ok) throw new Error(`Structured search rules: ${response.status}`);
      return response.json();
    })
    .catch(() => ({ categories: [], audiences: [], properties: [] }));

  const explicitTitleAudience = (title, audience) => {
    const raw = String(title || '');
    if (audience === 'herren') {
      return /(^|[\s/_-])M([\s/_-]|$)/i.test(raw)
        && !/(^|[\s/_-])W([\s/_-]|$)|\bLADY\b|\bWOMEN\b|\bJR\b|\bKIDS?\b/i.test(raw);
    }
    if (audience === 'damen') {
      return /(^|[\s/_-])W([\s/_-]|$)|\bLADY\b|\bWOMEN\b/i.test(raw);
    }
    if (audience === 'kinder') {
      return /(^|[\s/_-])JR([\s/_-]|$)|\bJUNIOR\b|\bKIDS?\b/i.test(raw);
    }
    return true;
  };

  const audienceMatches = (item, rule) => {
    if (!rule) return true;
    const corpus = normalize(item.dataset.searchAudience || '');
    const positives = (rule.positiveTokens || []).some(token => containsPhrase(corpus, token));
    const negatives = (rule.negativeTokens || []).some(token => containsPhrase(corpus, token));
    if (positives && !negatives) return true;
    if (negatives && !positives) return false;
    return explicitTitleAudience(item.dataset.searchTitle, rule.canonical);
  };

  const categoryScore = (item, rule) => {
    const category = normalize(item.dataset.searchCategory || '');
    const corpus = normalize(item.dataset.searchCorpus || '');
    let score = 70;

    if (category.includes(`category ${normalize(rule.canonical)}`)) score += 100;
    if ((rule.categoryTokens || []).some(token => containsPhrase(category, token))) score += 90;
    if ((rule.supportingTokens || []).some(token => containsPhrase(corpus, token))) score += 25;

    const conflict = (rule.conflictingTokens || []).some(
      token => containsPhrase(category, token) || containsPhrase(corpus, token)
    );
    const support = (rule.supportingTokens || []).some(token => containsPhrase(corpus, token));
    if (conflict && !support) score -= 140;

    return score;
  };

  const propertyMatches = (item, rule) => {
    if (!rule) return true;
    const properties = normalize(item.dataset.searchProperties || '');
    const positive = (rule.positiveTokens || []).some(token => containsPhrase(properties, token));
    if (positive) return true;

    const insufficient = (rule.insufficientTokens || []).some(token => containsPhrase(properties, token));
    if (insufficient) return false;

    return false;
  };

  const escapeHtml = (value) => String(value).replace(
    /[&<>"']/g,
    character => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    }[character])
  );

  const renderEmpty = (instance, query) => {
    instance.predictiveSearchResults.innerHTML = `
      <div class="f-predictive-search__empty" role="status">
        <p>Keine fachlich passenden Produkte für „${escapeHtml(query)}“ gefunden.</p>
        <p>Bitte entferne eine Eigenschaft, Größe oder Zielgruppe aus der Suche.</p>
      </div>`;
  };

  customElements.whenDefined('predictive-search').then(async () => {
    const SearchClass = customElements.get('predictive-search');
    if (!SearchClass || SearchClass.prototype.__peakStyleStructuredSearch) return;

    SearchClass.prototype.__peakStyleStructuredSearch = true;
    const originalGetSearchResults = SearchClass.prototype.getSearchResults;

    SearchClass.prototype.getSearchResults = async function(rawQuery) {
      const rules = await rulesPromise;
      const categoryRule = findRule(rawQuery, rules.categories);
      if (!categoryRule) return originalGetSearchResults.call(this, rawQuery);

      const audienceRule = findRule(rawQuery, rules.audiences);
      const explicitPropertyRule = findRule(rawQuery, rules.properties);
      const requiredPropertyRule = categoryRule.requiredProperty
        ? (rules.properties || []).find(rule => rule.canonical === categoryRule.requiredProperty) || null
        : null;
      const propertyRule = explicitPropertyRule || requiredPropertyRule;
      const parsed = typeof this.parseQuery === 'function'
        ? this.parseQuery(rawQuery)
        : { size: '', colorKey: '' };

      const requestId = ++this.requestSequence;
      this.abortPendingRequest?.();
      this.setLiveRegionLoadingState?.();
      this.abortController = new AbortController();

      try {
        const url = `/collections/${encodeURIComponent(categoryRule.collectionHandle)}?section_id=quick-search-collection-products`;
        const response = await fetch(url, {
          signal: this.abortController.signal,
          headers: { Accept: 'text/html' }
        });
        if (!response.ok) throw new Error(`Structured collection search: ${response.status}`);

        const html = await response.text();
        if (requestId !== this.requestSequence || rawQuery !== this.getQuery()) return;

        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const section = documentFragment.querySelector('#shopify-section-quick-search-collection-products');
        if (!section) throw new Error('Structured search section missing');

        this.predictiveSearchResults.innerHTML = section.innerHTML;

        const candidates = [...this.querySelectorAll('[data-structured-product]')]
          .map(item => ({ item, score: categoryScore(item, categoryRule) }))
          .filter(entry => entry.score > 0)
          .filter(entry => audienceMatches(entry.item, audienceRule))
          .filter(entry => propertyMatches(entry.item, propertyRule))
          .sort((a, b) => b.score - a.score);

        const maxVisible = window.matchMedia('(max-width: 767px)').matches ? 6 : 12;
        const visibleSet = new Set(candidates.slice(0, maxVisible).map(entry => entry.item));
        this.querySelectorAll('[data-structured-product]').forEach(item => {
          item.hidden = !visibleSet.has(item);
        });

        if (!candidates.length) {
          renderEmpty(this, rawQuery);
        } else if (typeof this.applyVariantCriteria === 'function') {
          await this.applyVariantCriteria(parsed);
        }

        if (requestId !== this.requestSequence || rawQuery !== this.getQuery()) return;

        const count = [...this.querySelectorAll('[data-structured-product]')]
          .filter(item => !item.hidden).length;
        const liveCount = this.querySelector('[data-predictive-search-live-region-count-value]');
        if (liveCount) liveCount.textContent = `${count} passende Produkte.`;

        this.setAttribute('results', 'true');
        this.removeAttribute('loading');
        this.setLiveRegionText?.(liveCount?.textContent || '');
        this.open();
      } catch (error) {
        if (error.name !== 'AbortError') {
          originalGetSearchResults.call(this, rawQuery);
        }
      } finally {
        if (requestId === this.requestSequence) this.abortController = null;
      }
    };
  });
})();